/**
 * 会場ピンの常設表示（design §11-5 / 要件21）。
 *
 * §15 の会場プリセットを静的 GeoJSON として symbol レイヤーで常設表示する。
 * 参加者ピン（DOM Marker）とは別レイヤー・別スタイル。可視制御・ポーリングの影響を受けない。
 * クリックで会場名ポップアップ。
 */
import { Popup, type Map as MaplibreMap, type MapGeoJSONFeature } from 'maplibre-gl';
import { VENUES, venueCatalogUrl, venuesToGeoJSON } from './venues';
import type { Lang } from '../i18n/lang';
import { messages } from '../i18n/messages';

const SOURCE_ID = 'venues';
const LAYER_ID = 'venues-symbol';

/**
 * 会場ポップアップで使う現在言語。setVenueLanguage で更新し、クリック時のポップアップ生成が参照する。
 * ラベル（symbol の text-field）とポップアップの主従をこれで揃える。
 */
let currentLang: Lang = 'ja';

/** アイコン画像の一辺（px）。devicePixelRatio を掛けて高解像度で描く。 */
const ICON_SIZE = 44;

/**
 * 絵文字を描いた画像データを生成する。
 * ALS の SDF グリフには絵文字が無く 404 になるため、絵文字は icon-image として
 * 自前のラスター画像で表示する（text-field には会場名だけを残す）。
 */
function renderEmojiImage(emoji: string): ImageData | null {
  const dpr = Math.max(1, Math.min(3, Math.round(window.devicePixelRatio || 1)));
  const size = ICON_SIZE * dpr;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }
  ctx.clearRect(0, 0, size, size);
  ctx.font = `${Math.floor(size * 0.72)}px system-ui, "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, size / 2, size / 2 + size * 0.04);
  return ctx.getImageData(0, 0, size, size);
}

/**
 * 会場絵文字アイコンを map に登録する（未登録のものだけ）。
 * setStyle 後は addImage の画像が消えるため、スタイル切替時は styledata で
 * ensureVenueLayer（内部で本関数を呼ぶ）を再実行して登録し直す（MapView 参照）。
 */
export function ensureVenueIcons(map: MaplibreMap): void {
  const dpr = Math.max(1, Math.min(3, Math.round(window.devicePixelRatio || 1)));
  for (const v of VENUES) {
    const iconId = `venue-${v.id}`;
    if (map.hasImage(iconId)) {
      continue;
    }
    const img = renderEmojiImage(v.emoji);
    if (img) {
      // pixelRatio を渡すと高解像度画像を論理サイズで扱える。
      map.addImage(iconId, img, { pixelRatio: dpr });
    }
  }
}

/**
 * 会場レイヤーに対するイベントハンドラを登録済みの map。
 *
 * ソース/レイヤー/アイコンは setStyle で消えるため再追加が必要だが、`map.on(..., LAYER_ID, ...)`
 * のハンドラは map 側に残る。再登録するとポップアップが二重に開くので一度だけ登録する。
 */
const handlersBound = new WeakSet<MaplibreMap>();

/**
 * 会場ソース/レイヤーを（無ければ）追加する。map load 後に呼ぶ。
 * スタイル切替（setStyle）後の再構築でも同じ関数を呼べる。
 */
export function ensureVenueLayer(map: MaplibreMap): void {
  if (map.getSource(SOURCE_ID)) {
    return;
  }
  // 先に絵文字アイコンを登録（icon-image から参照する）。
  ensureVenueIcons(map);

  map.addSource(SOURCE_ID, {
    type: 'geojson',
    data: venuesToGeoJSON(),
  });

  map.addLayer({
    id: LAYER_ID,
    type: 'symbol',
    source: SOURCE_ID,
    layout: {
      // 絵文字は icon-image（自前ラスター）で表示し、text-field は会場名だけにする。
      // こうすることで ALS に無い絵文字グリフを引きにいかず 404 を出さない。
      'icon-image': ['get', 'iconId'],
      'icon-size': 1,
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
      'text-field': ['get', 'name'],
      'text-size': 13,
      'text-offset': [0, 0.4],
      'text-anchor': 'top',
      'text-allow-overlap': false,
      'text-font': ['Noto Sans Regular'],
    },
    paint: {
      'text-color': '#1a365d',
      'text-halo-color': '#ffffff',
      'text-halo-width': 1.5,
    },
  });

  // クリック/カーソルのハンドラは map 単位で一度だけ登録する（スタイル切替での重複防止）。
  if (handlersBound.has(map)) {
    return;
  }
  handlersBound.add(map);

  // クリックで会場名ポップアップ
  map.on('click', LAYER_ID, (e) => {
    const feature = e.features?.[0] as MapGeoJSONFeature | undefined;
    if (!feature) {
      return;
    }
    const props = feature.properties as {
      id: string;
      name: string;
      nameEn: string;
      emoji: string;
    };
    const coords = (feature.geometry as GeoJSON.Point).coordinates as [number, number];
    // 現在言語を主、もう一方を従（小さく併記）にする。
    const primary = currentLang === 'ja' ? props.name : props.nameEn;
    const secondary = currentLang === 'ja' ? props.nameEn : props.name;
    // セッションがある会場には公式カタログ（会場で絞り込み済み）へのリンクを添える（タスク45）。
    // URL・文言はすべて定数由来（ユーザー入力なし）なので setHTML のままでよい。
    const venue = VENUES.find((v) => v.id === props.id);
    const catalogUrl = venue ? venueCatalogUrl(venue) : null;
    const m = messages(currentLang);
    const catalogHtml = catalogUrl
      ? `<a href="${catalogUrl}" target="_blank" rel="noopener noreferrer" style="display:inline-block;margin-top:6px;font-weight:600;color:#2b6cb0;">${m.venue.catalogLink}</a><br><span style="font-weight:400;font-size:11px;color:#718096;">${m.venue.catalogNote}</span>`
      : '';
    new Popup({ closeButton: true, offset: 12 })
      .setLngLat(coords)
      .setHTML(
        `<div style="font:600 13px/1.4 system-ui,sans-serif;">${props.emoji} ${primary}<br><span style="font-weight:400;color:#666;">${secondary}</span>${catalogHtml ? `<br>${catalogHtml}` : ''}</div>`,
      )
      .addTo(map);
  });

  map.on('mouseenter', LAYER_ID, () => {
    map.getCanvas().style.cursor = 'pointer';
  });
  map.on('mouseleave', LAYER_ID, () => {
    map.getCanvas().style.cursor = '';
  });
}

/** 会場ラベルの言語を切り替える（日本語/英語）。絵文字は icon-image なので text のみ切替。 */
export function setVenueLanguage(map: MaplibreMap, lang: Lang): void {
  // クリックポップアップの主従もこの言語に揃える。
  currentLang = lang;
  if (!map.getLayer(LAYER_ID)) {
    return;
  }
  const field = lang === 'ja' ? 'name' : 'nameEn';
  map.setLayoutProperty(LAYER_ID, 'text-field', ['get', field]);
}
